import React, { useMemo } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Pill from "../../../atoms/CardPill";
import {
  getACMGVerdict,
  getCardsIsCompact,
} from "../../../../state/selectors/CardsUI.selectors";
import {
  getAcmgDefaultVerdictRules,
  getACMGThrottling,
} from "../../../../state/selectors/ACMGComponent.selectors";
import CardWrapper from "../../CardWrapper";
import Card from "../../Card";
import { getPathogenicityColorByAllNames } from "../../../../common/helpers/Constant";
import {
  getACMGClassifierHasErrors,
  getIsUserPremiumOrClinical,
  getVariantId,
  getVariantResult,
} from "../../../../state/selectors/general.selectors";
import ACMGScores from "../../../../pages/variant/components/acmg/acmg-scores";

const ACMGCard = ({
  card,
  acmgVerdict,
  isCompact,
  acmgThrottlingInfo,
  acmgErrors,
  variantId,
  acmgVerdictRules,
  variantResult,
  isUserPremiumOrClinical,
}) => {
  const pathoColor = getPathogenicityColorByAllNames("pathogenic");
  const isDisabled =
    !Object.keys(acmgVerdict || {}).length && !acmgThrottlingInfo;
  const color = useMemo(
    () => getPathogenicityColorByAllNames(acmgVerdict) || card.color,
    [variantId, variantResult]
  );

  const memoizedACMGVerdict = useMemo(
    () => acmgVerdict,
    [variantId, variantResult]
  );
  const memoizedACMGVerdictRules = useMemo(
    () => acmgVerdictRules,
    [variantId, variantResult]
  );

  const secondaryTitle =
    isCompact && acmgVerdict && !acmgErrors ? (
      <b className="hide-in-tooltip" style={{ color }}>
        {memoizedACMGVerdict}
      </b>
    ) : null;

  return (
    <CardWrapper
      {...card}
      isDisabled={isDisabled}
      color={acmgErrors ? pathoColor : color}
      secondaryTitle={secondaryTitle}
    >
      {memoizedACMGVerdict && !acmgErrors && isUserPremiumOrClinical && (
        <div data-testid="acmg-card-content">
          <Pill inverted={false} verdict={memoizedACMGVerdict} />
          <ACMGScores
            showExplanations={false}
            verdictRules={memoizedACMGVerdictRules}
            customFontSize="tw-text-2xs tw-relative tw--top-0.5 tw-ml-1"
          />
        </div>
      )}
      {acmgErrors && (
        <div data-testid="acmg-errors">
          <Pill inverted={false} verdict="Error" backgroundColor={pathoColor} />
        </div>
      )}
    </CardWrapper>
  );
};

ACMGCard.propTypes = {
  acmgVerdict: PropTypes.string,
  acmgErrors: PropTypes.bool,
  card: PropTypes.instanceOf(Card).isRequired,
  isCompact: PropTypes.bool.isRequired,
  acmgThrottlingInfo: PropTypes.shape({
    throttling_msg: PropTypes.string,
  }),
  variantId: PropTypes.string,
  acmgVerdictRules: PropTypes.shape({
    approx_score: PropTypes.number,
    benign_subscore: PropTypes.string,
    clinical_score: PropTypes.number,
    pathogenic_subscore: PropTypes.string,
    verdict: PropTypes.string,
    benign_score: PropTypes.number,
    pathogenic_score: PropTypes.number,
    total_score: PropTypes.number,
  }),
  variantResult: PropTypes.shape({}),
  isUserPremiumOrClinical: PropTypes.bool,
};

ACMGCard.defaultProps = {
  acmgVerdict: "",
  acmgThrottlingInfo: null,
  acmgErrors: false,
  variantId: null,
  acmgVerdictRules: null,
  variantResult: {},
  isUserPremiumOrClinical: false,
};

const mapStateToProps = (state) => ({
  acmgVerdict: getACMGVerdict(state),
  isCompact: getCardsIsCompact(state),
  acmgThrottlingInfo: getACMGThrottling(state),
  acmgErrors: getACMGClassifierHasErrors(state),
  variantId: getVariantId(state),
  acmgVerdictRules: getAcmgDefaultVerdictRules(state),
  variantResult: getVariantResult(state),
  isUserPremiumOrClinical: getIsUserPremiumOrClinical(state),
});

export default connect(mapStateToProps)(ACMGCard);
